import { Shell } from '@/components/Shell';
import { adminApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/customers/')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: CustomersPage,
});

function CustomersPage() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'customers', q],
    queryFn: () => adminApi.customers({ q: q || undefined }),
  });
  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short' });
  const count = data?.paging.count ?? 0;

  return (
    <Shell page="CLIENTES" sub={`DIRECTÓRIO · ${count} REGISTOS`}>
      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="search"
          placeholder="Procurar por email, nome ou telefone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '1px solid var(--lv-line)',
            background: '#fff',
            fontSize: 13,
            borderRadius: 0,
          }}
        />
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid var(--lv-line)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--lv-night)' }}>
              {['CLIENTE', 'TELEFONE', 'PAÍS', 'REGISTO', 'RESERVAS'].map((h) => (
                <th
                  key={h}
                  className="label"
                  style={{
                    color: 'var(--lv-brand-glow)',
                    padding: '12px 16px',
                    textAlign: h === 'RESERVAS' ? 'right' : 'left',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  A carregar…
                </td>
              </tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  Sem clientes para esta procura.
                </td>
              </tr>
            )}
            {data?.items.map((u) => {
              const initials = (u.name ?? u.email)
                .split(' ')
                .map((p) => p[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              return (
                <tr
                  key={u.id}
                  className="row-link"
                  style={{ borderTop: '1px solid var(--lv-line)' }}
                  onClick={() => navigate({ to: '/customers/$id', params: { id: u.id } })}
                >
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          background: 'var(--lv-brand-deep)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                      <div>
                        <Link
                          to="/customers/$id"
                          params={{ id: u.id }}
                          style={{
                            fontWeight: 700,
                            color: 'var(--lv-brand)',
                            textDecoration: 'none',
                            fontSize: 13,
                          }}
                        >
                          {u.name ?? u.email}
                        </Link>
                        {u.name && (
                          <div style={{ fontSize: 11, color: 'var(--lv-gray-500)', marginTop: 1 }}>
                            {u.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '13px 16px', fontSize: 12 }}>
                    {u.phone ?? <span style={{ color: 'var(--lv-gray-500)' }}>—</span>}
                  </td>
                  <td style={{ padding: '13px 16px', fontSize: 12 }}>
                    {u.country ?? <span style={{ color: 'var(--lv-gray-500)' }}>—</span>}
                  </td>
                  <td style={{ padding: '13px 16px', fontSize: 12, color: 'var(--lv-gray-500)' }}>
                    {dateFmt.format(new Date(u.createdAt))}
                  </td>
                  <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                    <span
                      className="mono"
                      style={{
                        display: 'inline-block',
                        minWidth: 28,
                        padding: '3px 8px',
                        fontSize: 12,
                        fontWeight: 700,
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        background: u.bookingsCount > 0 ? 'var(--lv-brand)' : 'var(--lv-surface-soft)',
                        color: u.bookingsCount > 0 ? '#fff' : 'var(--lv-gray-500)',
                        border: '1px solid var(--lv-line)',
                      }}
                    >
                      {u.bookingsCount}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {data && (
          <div
            style={{
              borderTop: '1px solid var(--lv-line)',
              padding: '12px 16px',
              fontSize: 12,
              color: 'var(--lv-gray-500)',
            }}
          >
            Mostrando {data.items.length} de {count}
          </div>
        )}
      </div>
    </Shell>
  );
}
